import ModalCst from "@components/ModalCst";
import { Box, Button, Grid, Typography, useTheme } from "@mui/material";
import { ActionUploadDownloadIterface } from "@components/ModalDownloadUploadCst/types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ButtonCst from "@components/ButtonCst";
import UploaderDropZoneCst from "@components/ModalDownloadUploadCst/components/UploaderDropZoneCst";
import TooltipInput from "@components/InputCst/TooltipInput";
import { AxiosResponse } from "axios";
import { useAuth } from "@context/auth";
import { MultiSelectCst, SelectCst } from "@components/InputCst";
import {
  downloadFile,
  onErrorMessage,
  ServicesURL,
  uploadFile,
} from "@utils/utilsApi";
import { ActionsType } from "src/interfaces/Common";
import { useParams } from "react-router-dom";
import { MethodType } from "src/services/type";

interface ModalDownloadUploadMultilanguageCstInterface {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectOption?: any;
  type: string;
}

export interface MultilanguageActionUploadDownloadIterface extends ActionsType {
  types: string[];
  permissionUpload?: string;
  tooltipFile?: boolean;
  tooltipFileIta?: string;
  tooltipFileEn?: string;
  downloadDisabled?: boolean;
}

const ModalDownloadUploadMultilanguageCst: React.FC<
  ModalDownloadUploadMultilanguageCstInterface
> = ({ open, setOpen, selectOption, type }) => {
  const theme = useTheme();
  const { t: tModal } = useTranslation(["modal"]);
  const { t } = useTranslation(["upload-component"]);
  const { id = "1", IdProfession, IdKnowledgeArea } = useParams();
  const [errorResponse, setErrorResponse] = useState<any>([]);
  const [apiResponse, setApiResponse] = useState<AxiosResponse | null>(null);
  const [languagesSelected, setLanguagesSelected] = useState<string[]>([]);

  const multiLanguageConfig:
    | MultilanguageActionUploadDownloadIterface
    | undefined = {
    types: ["upload", "download", "template"],
    type: "upload",
    permission: "skillprofession.skills.download",
    permissionUpload: "skillprofession.skills.save",
    accept: "text/csv",
    icon: "upload",
    labelTranslate: `${t("skills.multilanguage.title", {
      ns: "menu",
    })}`,
    label: { en: "Languages", it: "Lingue" },
    service: "skills",
    tooltipFile: false,
  };

  const title =
    multiLanguageConfig && multiLanguageConfig.labelTranslate
      ? multiLanguageConfig.labelTranslate
      : "";

  const isDownload =
    multiLanguageConfig.types.findIndex((el: string) =>
      el.includes("download"),
    ) >= 0;

  const isTemplate =
    multiLanguageConfig.types.findIndex((el: string) =>
      el.includes("template"),
    ) >= 0;
  const { user } = useAuth();

  let canUpload: boolean = true;
  if (multiLanguageConfig.permissionUpload) {
    canUpload = false;
    if (
      user?.permissions.find(
        (permission) => permission === multiLanguageConfig.permissionUpload,
      )
    ) {
      canUpload = true;
    }
  }

  const { tooltipFile, tooltipFileIta, tooltipFileEn } = multiLanguageConfig;

  useEffect(() => {
    if (selectOption?.valueOptions && selectOption?.valueOptions.length > 0) {
      setLanguagesSelected(selectOption?.valueOptions || []);
    }
  }, [selectOption]);

  const onDownload = async (el: string) => {
    resetModal();
    const languages = languagesSelected
      .map((language: any) => {
        return language.value;
      })
      .join();
    if (el === "template") {
      downloadFile(
        `skills/template/${type}?languages=${languages}&corporateId=${id}${IdProfession || IdKnowledgeArea ? `&professionParentId=${IdKnowledgeArea || IdProfession}` : ""}`,
      );
    } else {
      const response: any = await downloadFile(
        `skills/download/${type}?languages=${languages}&corporateId=${id}${IdProfession || IdKnowledgeArea ? `&professionParentId=${IdKnowledgeArea || IdProfession}` : ""}`,
      );

      if (response?.response?.status >= 400) {
        const message = response?.data?.errors?.UNKNOWN ||
          response?.response?.data?.errors?.FATAL ||
          response?.response?.data?.errors?.UNKNOWN ||
          response?.data?.errors?.FATAL || [response?.data?.errorMessage];
        setErrorResponse(message);
      }
    }
  };

  const onUpload: any = (formData: FormData) => {
    resetModal();
    return uploadFile(
      `skills/upload/${type}?corporateId=${id}`,
      formData,
      MethodType.PUT,
    );
  };

  const resetModal = () => {
    setErrorResponse([]);
    setApiResponse(null);
  };

  return (
    <ModalCst open={open} setOpen={setOpen}>
      <>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              maxWidth: "80%",
            }}
          >
            <h2 style={{ margin: "0" }} id="parent-modal-title">
              {`${tModal("upload")} ${(title || "").toLowerCase()}`}
            </h2>
            {tooltipFile && (
              <TooltipInput
                hrefInput
                hrefIta={tooltipFileIta}
                hrefEn={tooltipFileEn}
              />
            )}
          </Box>
          <Button
            onClick={() => {
              resetModal();
              setOpen(false);
            }}
            sx={{
              borderRadius: "50%",
              minWidth: "0",
              padding: "0 3px",
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              span: {
                width: "0",
              },
            }}
          >
            <CloseIcon fontSize="large" />
          </Button>
        </Box>
        <Grid
          container
          spacing={4}
          sx={{
            minHeight: "300px",
            paddingTop: "70px",
          }}
        >
          <Grid xs={12} lg={6} item>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                rowGap: "20px",
                padding: "15px",
                border: `1px solid ${theme.palette.primary.main}90`,
                borderRadius: "4px",
              }}
            >
              {selectOption && (
                <MultiSelectCst
                  id={"multilanguage_multiselect"}
                  label={selectOption.header}
                  value={languagesSelected}
                  optionsToDisable={selectOption.optionsToDisable}
                  disabled={selectOption.disabled}
                  service={selectOption.service}
                  onChange={(e: any) => {
                    let newValue = JSON.parse(JSON.stringify(e));
                    setLanguagesSelected(newValue);
                  }}
                  options={[]}
                  required={selectOption.required}
                />
              )}
              {isTemplate && (
                <ButtonCst
                  sx={{
                    width: "100%",
                  }}
                  id="add-file"
                  variant={"contained"}
                  onClick={() => onDownload("template")}
                  disabled={languagesSelected.length === 0}
                >
                  <DownloadIcon /> {t("download-template")}
                </ButtonCst>
              )}
            </Box>
          </Grid>
          <Grid
            xs={12}
            lg={6}
            item
            sx={{
              display: "flex",
              flexDirection: "column",
              rowGap: "20px",
              justifyContent: "center",
            }}
          >
            {isDownload && (
              <ButtonCst
                sx={{
                  width: "100%",
                }}
                id="add-file"
                variant={"outlined"}
                onClick={() => onDownload("download")}
              >
                <DownloadIcon /> {t("download")}
              </ButtonCst>
            )}
            {canUpload && (
              <Grid
                xs={12}
                lg={12}
                // key={multiLanguageConfig.types}
                item
                sx={{ textAlign: "center", width: "100%" }}
              >
                <UploaderDropZoneCst
                  accept={multiLanguageConfig.accept}
                  data={multiLanguageConfig}
                  callBack={(File: FormData) => {
                    return onUpload(File);
                  }}
                  errorResponse={(error: any) => setErrorResponse(error)}
                  setApiResponse={setApiResponse}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        {apiResponse &&
          apiResponse?.headers["content-disposition"] &&
          apiResponse?.headers["content-disposition"].includes("filename") && (
            <Box
              sx={{
                maxHeight: "200px",
                overflow: "auto",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Typography>{`${t("OKwithErrors")}`}</Typography>
              <ButtonCst
                id={"download-csv"}
                variant={"outlined"}
                icon={"download"}
                size={"medium"}
                sx={{
                  minWidth: "50px",
                  mr: "auto",
                  marginLeft: "20px",
                }}
                onClick={() => {
                  const rawType = apiResponse.headers["content-type"];
                  const type =
                    typeof rawType === "string" ? rawType : undefined;
                  const blob = new Blob([apiResponse.data], { type: type });
                  let downloadUrl = URL.createObjectURL(blob);

                  const link = document.createElement("a");
                  link.setAttribute("target", "_blank");
                  document.body.appendChild(link);
                  link.href = downloadUrl;
                  link.download =
                    apiResponse?.headers["content-disposition"].split(
                      "filename=",
                    )[1];
                  link.click();
                  document.body.removeChild(link);
                }}
              />
            </Box>
          )}
        {errorResponse && (
          <Box sx={{ maxHeight: "200px", overflow: "auto" }}>
            {errorResponse &&
              errorResponse.map((item: any, index: any) => {
                return (
                  <Typography key={index}>
                    {item?.message ? item?.message : item}
                  </Typography>
                );
              })}
          </Box>
        )}
      </>
    </ModalCst>
  );
};

export default ModalDownloadUploadMultilanguageCst;
